import React, { useEffect, useMemo, useState } from "react";
import {
  onAuthStateChanged,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "firebase/auth";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import {
  educationAuth,
  educationDb,
} from "../firebase.js";

const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,400;8..60,500;8..60,600;8..60,700&family=Inter:wght@400;500;600;700;800&family=IBM+Plex+Mono:wght@500;600&display=swap');

  :root {
    --bg:          #f6f4ee;
    --surface:     #ffffff;
    --surface-2:   #fbf9f4;
    --border:      #e6e2d6;
    --border-soft: #edeae0;
    --ink:         #161a20;
    --ink-soft:    #4b5160;
    --muted:       #80838f;
    --navy:        #17233c;
    --navy-2:      #223252;
    --navy-soft:   #eef1f6;
    --gold:        #a8792a;
    --gold-soft:   #f2e6c9;
    --gold-soft-2: #fbf3df;
    --ok:          #1f7a5c;
    --ok-soft:     #e4f3ec;
    --err:         #b3261e;
    --err-soft:    #fbe9e7;
  }

  * { box-sizing: border-box; }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .profile-shell {
    animation: fadeInPage .28s ease;
  }

  @keyframes fadeInPage {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .ui-hover-lift {
    transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease, background .2s ease;
  }

  .ui-hover-lift:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 32px rgba(23, 35, 60, 0.07);
  }

  .profile-tab-fancy,
  .sidebar-fancy-btn,
  .primary-btn-fancy,
  .secondary-btn-fancy,
  .dashboard-card-fancy {
    transition: all .2s ease;
  }

  .profile-tab-fancy:hover,
  .primary-btn-fancy:hover:not(:disabled),
  .secondary-btn-fancy:hover:not(:disabled),
  .dashboard-card-fancy:hover {
    transform: translateY(-1px);
  }

  .sidebar-fancy-btn:hover {
    transform: translateX(2px);
  }

  .field-input-fancy,
  .textarea-fancy,
  .select-fancy {
    transition: border-color .2s ease, box-shadow .2s ease, background .2s ease;
  }

  .field-input-fancy:focus,
  .textarea-fancy:focus,
  .select-fancy:focus {
    outline: none;
    border-color: #a8792a !important;
    box-shadow: 0 0 0 4px rgba(168, 121, 42, 0.12);
    background: #ffffff !important;
  }

  @media (max-width: 1120px) {
    .layout-responsive {
      grid-template-columns: 1fr !important;
    }

    .sidebar-responsive {
      position: static !important;
      top: auto !important;
    }

    .cards-grid-responsive {
      grid-template-columns: repeat(2, 1fr) !important;
    }

    .profile-layout-responsive {
      grid-template-columns: 1fr !important;
    }

    .profile-sidecard-responsive {
      display: flex !important;
      align-items: center !important;
      gap: 16px !important;
      text-align: left !important;
    }

    .avatar-wrap-responsive {
      margin: 0 !important;
      flex-shrink: 0 !important;
    }
  }

  @media (max-width: 780px) {
    .cards-grid-responsive {
      grid-template-columns: 1fr !important;
    }

    .details-row-responsive {
      grid-template-columns: 1fr !important;
      gap: 6px !important;
    }

    .form-grid-responsive {
      grid-template-columns: 1fr !important;
    }

    .calendar-grid-responsive {
      grid-template-columns: repeat(7, minmax(0, 1fr)) !important;
      gap: 6px !important;
    }

    .page-padding-responsive {
      padding: 12px !important;
    }

    .profile-maincard-responsive {
      padding: 16px !important;
    }

    .profile-tabs-responsive {
      gap: 8px !important;
    }

    .profile-tab-btn-responsive {
      width: 100% !important;
      justify-content: center !important;
    }

    .page-title-mobile {
      font-size: 26px !important;
    }
  }
`;

const TIME_SLOTS = [
  "08:00 - 10:00",
  "10:00 - 12:00",
  "14:00 - 16:00",
  "16:00 - 18:00",
  "18:00 - 20:00",
];

const WEEK_DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

function formatDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatPrettyDate(dateKey) {
  const [y, m, d] = dateKey.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getMonthLabel(date) {
  return date.toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
  });
}

function getCalendarDays(monthDate) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  const startWeekDay = (firstDay.getDay() + 6) % 7;
  const totalDays = lastDay.getDate();

  const days = [];

  for (let i = 0; i < startWeekDay; i += 1) {
    days.push(null);
  }

  for (let day = 1; day <= totalDays; day += 1) {
    days.push(new Date(year, month, day));
  }

  return days;
}

function sanitizeCalendarAvailability(availability) {
  if (!availability || typeof availability !== "object") return {};

  const cleaned = {};

  Object.entries(availability).forEach(([dateKey, value]) => {
    if (!dateKey) return;

    const horaire =
      typeof value?.horaire === "string" && value.horaire.trim()
        ? value.horaire.trim()
        : "";

    if (!horaire) return;

    cleaned[dateKey] = {
      horaire,
      statut: "disponible",
    };
  });

  return cleaned;
}

export default function Profile() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [activeTab, setActiveTab] = useState("profile");
  const [profileMode, setProfileMode] = useState("view");
  const navigate = useNavigate();

  const [editData, setEditData] = useState({
    prenom: "",
    nom: "",
    age: "",
    profession: "",
    residence: "",
    telephone: "",
    niveau: "",
    cursus: "",
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const [selectedSlot, setSelectedSlot] = useState(TIME_SLOTS[2]);
  const [calendarAvailability, setCalendarAvailability] = useState({});

  useEffect(() => {
    const unsub = onAuthStateChanged(educationAuth, async (u) => {
      setUser(u || null);

      if (!u) {
        setProfile(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setErrorMsg("");
        setSuccessMsg("");

        const ref = doc(educationDb, "educationUsers", u.uid);
        const snap = await getDoc(ref);

        if (snap.exists()) {
          const data = snap.data();
          setProfile({
            ...data,
            uid: data?.uid || u.uid,
            email: data?.email || u.email || "",
          });

          setEditData({
            prenom: data?.prenom || "",
            nom: data?.nom || "",
            age: data?.age ?? "",
            profession: data?.profession || "",
            residence: data?.residence || data?.lieuResidence || "",
            telephone:
              data?.telephone ||
              (data?.countryCode || data?.indicatif
                ? `${data?.countryCode || data?.indicatif} ${data?.phone || data?.numero || ""}`.trim()
                : ""),
            niveau: data?.niveau || "",
            cursus: data?.cursus || data?.bio || "",
          });

          setCalendarAvailability(
            data?.disponibilitesCalendrier &&
              typeof data.disponibilitesCalendrier === "object"
              ? data.disponibilitesCalendrier
              : {}
          );
        } else {
          setProfile(null);
          setErrorMsg("Profil introuvable. Veuillez compléter votre inscription.");
        }
      } catch (err) {
        console.error(err);
        setErrorMsg("Erreur lors du chargement du profil.");
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
  }, []);

  const phoneValue =
    profile?.telephone ||
    (profile?.countryCode || profile?.indicatif
      ? `${profile?.countryCode || profile?.indicatif} ${profile?.phone || profile?.numero || ""}`.trim()
      : "");

  const avatarUrl = profile?.photoURL || user?.photoURL || "";

  const initials = useMemo(() => {
    const p = (profile?.prenom || "").trim();
    const n = (profile?.nom || "").trim();
    const a = (p ? p[0] : "").toUpperCase();
    const b = (n ? n[0] : "").toUpperCase();
    return (a + b) || ((user?.email || "U")[0] || "U").toUpperCase();
  }, [profile?.prenom, profile?.nom, user?.email]);

  const fullName =
    `${profile?.prenom || ""} ${profile?.nom || ""}`.trim() || "Utilisateur";

  const cursusText =
    profile?.cursus ||
    profile?.bio ||
    "Complétez votre cursus et votre présentation pour enrichir votre profil.";

  const disponibilites = Array.isArray(profile?.disponibilites)
    ? profile.disponibilites
    : [];

  const sortedCalendarAvailability = Object.entries(calendarAvailability).sort(
    ([a], [b]) => new Date(a) - new Date(b)
  );

  const calendarDays = useMemo(
    () => getCalendarDays(calendarMonth),
    [calendarMonth]
  );

  const resetMessages = () => {
    setErrorMsg("");
    setSuccessMsg("");
  };

  const handleEditChange = (field, value) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePasswordChangeField = (field, value) => {
    setPasswordData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const openEditMode = () => {
    resetMessages();
    setProfileMode("edit");
  };

  const openPasswordMode = () => {
    resetMessages();
    setProfileMode("password");
  };

  const openViewMode = () => {
    resetMessages();
    setProfileMode("view");
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
  };

  const goToPreviousMonth = () => {
    setCalendarMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
    );
  };

  const goToNextMonth = () => {
    setCalendarMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
    );
  };

  const toggleCalendarDay = (date) => {
    if (!date) return;

    const key = formatDateKey(date);

    setCalendarAvailability((prev) => {
      const next = { ...prev };

      if (next[key]) {
        delete next[key];
      } else {
        next[key] = {
          horaire: selectedSlot,
          statut: "disponible",
        };
      }

      return next;
    });
  };

  const updateAvailabilitySlot = (dateKey, slot) => {
    setCalendarAvailability((prev) => ({
      ...prev,
      [dateKey]: {
        ...(prev[dateKey] || {}),
        horaire: slot,
        statut: "disponible",
      },
    }));
  };

  const removeAvailabilityDate = (dateKey) => {
    setCalendarAvailability((prev) => {
      const next = { ...prev };
      delete next[dateKey];
      return next;
    });
  };

  const handleSaveProfile = async () => {
    if (!user) return;

    try {
      setSavingProfile(true);
      resetMessages();

      const cleanedAvailability = sanitizeCalendarAvailability(calendarAvailability);

     const payload = {
  prenom: editData.prenom.trim(),
  nom: editData.nom.trim(),
  age: editData.age === "" ? null : Number(editData.age),
  profession: editData.profession.trim(),
  residence: editData.residence.trim(),
  telephone: editData.telephone.trim(),
  niveau: editData.niveau.trim(),
  cursus: editData.cursus.trim(),
  email: profile?.email || user.email || "",
  uid: user.uid,
  platform: "education",
  disponibilitesCalendrier: cleanedAvailability,
};

      await updateDoc(
        doc(educationDb, "educationUsers", user.uid),
        payload
      );

      const refreshedSnap = await getDoc(
        doc(educationDb, "educationUsers", user.uid)
      );
      if (refreshedSnap.exists()) {
        const refreshedData = refreshedSnap.data();

        setProfile({
          ...refreshedData,
          uid: refreshedData?.uid || user.uid,
          email: refreshedData?.email || user.email || "",
        });

        setCalendarAvailability(
          refreshedData?.disponibilitesCalendrier &&
            typeof refreshedData.disponibilitesCalendrier === "object"
            ? refreshedData.disponibilitesCalendrier
            : {}
        );
      }

      setSuccessMsg("✅ Profil et disponibilités mis à jour avec succès.");
      setProfileMode("view");
    } catch (err) {
      console.error(err);
      setErrorMsg("Erreur lors de la mise à jour du profil.");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleUpdatePassword = async () => {
    try {
      resetMessages();

      if (!user?.email) {
        setErrorMsg("Utilisateur non reconnu.");
        return;
      }

      if (
        !passwordData.currentPassword ||
        !passwordData.newPassword ||
        !passwordData.confirmPassword
      ) {
        setErrorMsg("Veuillez remplir tous les champs du mot de passe.");
        return;
      }

      if (passwordData.newPassword.length < 6) {
        setErrorMsg("Le nouveau mot de passe doit contenir au moins 6 caractères.");
        return;
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setErrorMsg("La confirmation du mot de passe ne correspond pas.");
        return;
      }

      setSavingPassword(true);

      const credential = EmailAuthProvider.credential(
        user.email,
        passwordData.currentPassword
      );

      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, passwordData.newPassword);

      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      setSuccessMsg("✅ Mot de passe modifié avec succès.");
      setProfileMode("view");
    } catch (err) {
      console.error(err);
      setErrorMsg(
        "Impossible de modifier le mot de passe. Vérifiez votre mot de passe actuel."
      );
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <>
        <style>{GLOBAL_CSS}</style>
        <div style={styles.loadingPage}>
          <div style={styles.loadingCard}>
            <div style={styles.loader} />
            <p style={styles.loadingText}>Chargement de votre espace...</p>
          </div>
        </div>
      </>
    );
  }

  if (!user) {
    return (
      <>
        <style>{GLOBAL_CSS}</style>
        <div style={styles.loadingPage}>
          <div style={styles.emptyCard}>
            <div style={styles.emptyIcon}>👤</div>
            <h3 style={styles.emptyTitle}>Connexion requise</h3>
            <p style={styles.emptyText}>
              Vous devez être connecté pour accéder à votre espace.
            </p>
          </div>
        </div>
      </>
    );
  }

  const renderMainContent = () => {
    switch (activeTab) {
      case "home":
        return (
          <>
            <button
              type="button"
              onClick={() => navigate("/education")}
              style={styles.topBackButton}
            >
              ← Retour à l’accueil
            </button>

            <div style={styles.welcomeBlock} className="ui-hover-lift">
              <div style={styles.welcomeHeader}>
                <div style={styles.welcomeIconWrap}>
                  <div style={styles.welcomeIcon}>🏠</div>
                </div>
                <div>
                  <h1 style={styles.pageTitle}>
                    Bienvenue, {profile?.prenom || "Utilisateur"}
                  </h1>
                  <p style={styles.pageSubtitle}>
                    Ravi de vous revoir sur votre espace tuteur.
                  </p>
                </div>
              </div>
            </div>

            <div style={styles.cardsGrid} className="cards-grid-responsive">
              <DashboardCard
                title="Séances à venir"
                tag="Planning"
                action="Consulter"
                subtitle="Vos prochaines séances"
                icon="🗓️"
                iconBg="#eef1f6"
                accent="#17233c"
                onClick={() => setActiveTab("upcoming")}
              />
              <DashboardCard
                title="Séances passées"
                tag="Historique"
                action="Historique"
                subtitle="Bilan des interventions"
                icon="🕘"
                iconBg="#f1efe6"
                accent="#5a5f6c"
                onClick={() => setActiveTab("past")}
              />
              <DashboardCard
                title="Positionnez-vous"
                tag="Opportunités"
                action="Candidater"
                subtitle="Séances disponibles"
                icon="☝️"
                iconBg="#f2e6c9"
                accent="#a8792a"
                onClick={() => setActiveTab("upcoming")}
              />
              <DashboardCard
                title="Messagerie"
                tag="Communication"
                action="Discuter"
                subtitle="Avec l'équipe"
                icon="💬"
                iconBg="#eef1f6"
                accent="#17233c"
                onClick={() => navigate("/contact")}
              />
              <DashboardCard
                title="Mon Profil"
                tag="Paramètres"
                action="Gérer"
                subtitle="Vos informations"
                icon="🪪"
                iconBg="#f1efe6"
                accent="#5a5f6c"
                onClick={() => setActiveTab("profile")}
              />
              <DashboardCard
                title="Mes Avis"
                tag="Évaluations"
                action="Consulter"
                subtitle="Vos évaluations"
                icon="🏅"
                iconBg="#f2e6c9"
                accent="#a8792a"
                onClick={() => setActiveTab("reviews")}
              />
            </div>
          </>
        );

      case "profile":
        return (
          <>
            <div style={styles.breadcrumbRow}>
              <span style={styles.breadcrumbLink}>Dashboard</span>
              <span style={styles.breadcrumbSep}>›</span>
              <span style={styles.breadcrumbCurrent}>Mon Profil</span>
            </div>

            <div style={styles.heroCard} className="ui-hover-lift">
              <div style={styles.profileHeaderBlock}>
                <div style={styles.profileHeaderIconWrap}>
                  <span style={styles.profileHeaderIcon}>🪪</span>
                </div>
                <div>
                  <h1 style={styles.pageTitleAlt} className="page-title-mobile">
                    {fullName}
                  </h1>
                  <p style={styles.pageSubtitleAlt}>
                    Gérez vos informations personnelles et vos disponibilités
                  </p>
                </div>
              </div>
            </div>

            <div
              style={styles.profileLayout}
              className="profile-layout-responsive"
            >
              <div
                style={styles.profileSideCard}
                className="profile-sidecard-responsive ui-hover-lift"
              >
                <div
                  style={styles.bigAvatarWrap}
                  className="avatar-wrap-responsive"
                >
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt="Photo de profil"
                      style={styles.bigAvatarImg}
                    />
                  ) : (
                    <div style={styles.bigAvatarFallback}>{initials}</div>
                  )}
                </div>

                <div>
                  <div style={styles.sideName}>{fullName}</div>
                  <div style={styles.sideMeta}>
                    🎓 {profile?.niveau || profile?.profession || "Profil utilisateur"}
                  </div>

                  <div style={styles.sideMiniStats}>
                    <div style={styles.sideMiniStat}>
                      <span style={styles.sideMiniStatLabel}>Ville</span>
                      <span style={styles.sideMiniStatValue}>
                        {profile?.residence ||
                          profile?.lieuResidence ||
                          "Non renseignée"}
                      </span>
                    </div>
                    <div style={styles.sideMiniStat}>
                      <span style={styles.sideMiniStatLabel}>Contact</span>
                      <span style={styles.sideMiniStatValue}>
                        {phoneValue || "Non renseigné"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div
                style={styles.profileMainCard}
                className="profile-maincard-responsive ui-hover-lift"
              >
                <div
                  style={styles.profileTabs}
                  className="profile-tabs-responsive"
                >
                  <button
                    className="profile-tab-fancy profile-tab-btn-responsive"
                    style={{
                      ...styles.profileTabBtn,
                      ...(profileMode === "view"
                        ? styles.profileTabBtnActive
                        : {}),
                    }}
                    onClick={openViewMode}
                  >
                    👤 Profil
                  </button>

                  <button
                    className="profile-tab-fancy profile-tab-btn-responsive"
                    style={{
                      ...styles.profileTabBtn,
                      ...(profileMode === "edit"
                        ? styles.profileTabBtnActive
                        : {}),
                    }}
                    onClick={openEditMode}
                  >
                    ✏️ Modifier
                  </button>

                  <button
                    className="profile-tab-fancy profile-tab-btn-responsive"
                    style={{
                      ...styles.profileTabBtn,
                      ...(profileMode === "password"
                        ? styles.profileTabBtnActive
                        : {}),
                    }}
                    onClick={openPasswordMode}
                  >
                    🔒 Mot de passe
                  </button>
                </div>

                {errorMsg && (
                  <div style={styles.alertError}>
                    <span style={{ marginRight: 8 }}>⚠</span>
                    {errorMsg}
                  </div>
                )}

                {successMsg && <div style={styles.alertSuccess}>{successMsg}</div>}

                {profileMode === "password" ? (
                  <>
                    <SectionTitle
                      icon="🔒"
                      title="Modifier votre mot de passe"
                    />

                    <div style={styles.passwordIntroBox}>
                      Pour sécuriser votre compte, choisissez un mot de passe solide.
                    </div>

                    <div
                      style={styles.formGrid}
                      className="form-grid-responsive"
                    >
                      <InputField
                        label="Mot de passe actuel"
                        type="password"
                        value={passwordData.currentPassword}
                        onChange={(value) =>
                          handlePasswordChangeField("currentPassword", value)
                        }
                      />
                      <InputField
                        label="Nouveau mot de passe"
                        type="password"
                        value={passwordData.newPassword}
                        onChange={(value) =>
                          handlePasswordChangeField("newPassword", value)
                        }
                      />
                      <InputField
                        label="Confirmer le nouveau mot de passe"
                        type="password"
                        value={passwordData.confirmPassword}
                        onChange={(value) =>
                          handlePasswordChangeField("confirmPassword", value)
                        }
                      />
                    </div>

                    <div style={styles.actionsRow}>
                      <button
                        style={styles.secondaryButton}
                        className="secondary-btn-fancy"
                        onClick={openViewMode}
                      >
                        Annuler
                      </button>
                      <button
                        style={styles.primaryButton}
                        className="primary-btn-fancy"
                        onClick={handleUpdatePassword}
                        disabled={savingPassword}
                      >
                        {savingPassword
                          ? "Mise à jour..."
                          : "Mettre à jour le mot de passe"}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <SectionTitle icon="📘" title="Cursus" />

                    {profileMode === "edit" ? (
                      <textarea
                        value={editData.cursus}
                        onChange={(e) =>
                          handleEditChange("cursus", e.target.value)
                        }
                        style={styles.textarea}
                        className="textarea-fancy"
                        placeholder="Décrivez votre cursus, votre parcours ou votre présentation."
                      />
                    ) : (
                      <div style={styles.cursusBox}>{cursusText}</div>
                    )}

                    <SectionTitle icon="ℹ️" title="Détails du profil" />

                    {profileMode === "edit" ? (
                      <>
                        <div
                          style={styles.formGrid}
                          className="form-grid-responsive"
                        >
                          <InputField
                            label="Nom"
                            value={editData.nom}
                            onChange={(value) => handleEditChange("nom", value)}
                          />
                          <InputField
                            label="Prénom"
                            value={editData.prenom}
                            onChange={(value) =>
                              handleEditChange("prenom", value)
                            }
                          />
                          <InputField
                            label="Âge"
                            type="number"
                            value={editData.age}
                            onChange={(value) => handleEditChange("age", value)}
                          />
                          <InputField
                            label="Profession"
                            value={editData.profession}
                            onChange={(value) =>
                              handleEditChange("profession", value)
                            }
                          />
                          <InputField
                            label="Niveau"
                            value={editData.niveau}
                            onChange={(value) =>
                              handleEditChange("niveau", value)
                            }
                          />
                          <InputField
                            label="Ville"
                            value={editData.residence}
                            onChange={(value) =>
                              handleEditChange("residence", value)
                            }
                          />
                          <InputField
                            label="Téléphone"
                            value={editData.telephone}
                            onChange={(value) =>
                              handleEditChange("telephone", value)
                            }
                          />
                          <InputField
                            label="Email"
                            value={profile?.email || user.email || ""}
                            disabled
                          />
                        </div>

                        <SectionTitle icon="📅" title="Disponibilités du mois" />

                        <div style={styles.calendarHeader}>
                          <button
                            type="button"
                            onClick={goToPreviousMonth}
                            style={styles.monthNavBtn}
                          >
                            ←
                          </button>

                          <div style={styles.calendarMonthLabel}>
                            {getMonthLabel(calendarMonth)}
                          </div>

                          <button
                            type="button"
                            onClick={goToNextMonth}
                            style={styles.monthNavBtn}
                          >
                            →
                          </button>
                        </div>

                        <div style={styles.slotSelectorBox}>
                          <label style={styles.fieldLabel}>
                            Créneau à appliquer lors du clic
                          </label>
                          <select
                            value={selectedSlot}
                            onChange={(e) => setSelectedSlot(e.target.value)}
                            style={styles.selectInput}
                            className="select-fancy"
                          >
                            {TIME_SLOTS.map((slot) => (
                              <option key={slot} value={slot}>
                                {slot}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div style={styles.calendarCard}>
                          <div style={styles.calendarWeekHeader}>
                            {WEEK_DAYS.map((day) => (
                              <div key={day} style={styles.calendarWeekCell}>
                                {day}
                              </div>
                            ))}
                          </div>

                          <div
                            style={styles.calendarGrid}
                            className="calendar-grid-responsive"
                          >
                            {calendarDays.map((date, index) => {
                              if (!date) {
                                return (
                                  <div
                                    key={`empty-${index}`}
                                    style={styles.calendarEmptyCell}
                                  />
                                );
                              }

                              const key = formatDateKey(date);
                              const isSelected = Boolean(calendarAvailability[key]);
                              const isToday = formatDateKey(new Date()) === key;

                              return (
                                <button
                                  key={key}
                                  type="button"
                                  onClick={() => toggleCalendarDay(date)}
                                  style={{
                                    ...styles.calendarDayBtn,
                                    ...(isSelected
                                      ? styles.calendarDayBtnSelected
                                      : {}),
                                    ...(isToday ? styles.calendarDayBtnToday : {}),
                                  }}
                                >
                                  <span style={styles.calendarDayNumber}>
                                    {date.getDate()}
                                  </span>
                                  {isSelected && (
                                    <span style={styles.calendarDaySlot}>
                                      {calendarAvailability[key]?.horaire}
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {sortedCalendarAvailability.length > 0 ? (
                          <div style={styles.selectedDaysBox}>
                            <div style={styles.selectedDaysTitle}>
                              Jours sélectionnés
                            </div>

                            <div style={styles.selectedDaysList}>
                              {sortedCalendarAvailability.map(
                                ([dateKey, data]) => (
                                  <div
                                    key={dateKey}
                                    style={styles.selectedDayItem}
                                  >
                                    <div style={styles.selectedDayLeft}>
                                      <div style={styles.selectedDayDate}>
                                        {formatPrettyDate(dateKey)}
                                      </div>

                                      <select
                                        value={data?.horaire || TIME_SLOTS[0]}
                                        onChange={(e) =>
                                          updateAvailabilitySlot(
                                            dateKey,
                                            e.target.value
                                          )
                                        }
                                        style={styles.selectedDaySelect}
                                        className="select-fancy"
                                      >
                                        {TIME_SLOTS.map((slot) => (
                                          <option key={slot} value={slot}>
                                            {slot}
                                          </option>
                                        ))}
                                      </select>
                                    </div>

                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeAvailabilityDate(dateKey)
                                      }
                                      style={styles.removeDayBtn}
                                    >
                                      Retirer
                                    </button>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        ) : (
                          <div style={styles.emptyInfoBox}>
                            Cliquez sur un ou plusieurs jours dans le calendrier
                            pour indiquer vos disponibilités.
                          </div>
                        )}

                        <div style={styles.actionsRow}>
                          <button
                            style={styles.secondaryButton}
                            className="secondary-btn-fancy"
                            onClick={openViewMode}
                          >
                            Annuler
                          </button>
                          <button
                            style={styles.primaryButton}
                            className="primary-btn-fancy"
                            onClick={handleSaveProfile}
                            disabled={savingProfile}
                          >
                            {savingProfile
                              ? "Enregistrement..."
                              : "Enregistrer les modifications"}
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={styles.detailsGrid}>
                          <DetailItem
                            label="Nom de naissance"
                            value={profile?.nom || "—"}
                          />
                          <DetailItem
                            label="Prénoms"
                            value={profile?.prenom || "—"}
                          />
                          <DetailItem
                            label="Âge"
                            value={profile?.age ? `${profile.age} ans` : "—"}
                          />
                          <DetailItem
                            label="Niveau"
                            value={
                              profile?.niveau || profile?.profession || "—"
                            }
                          />
                          <DetailItem
                            label="Ville"
                            value={
                              profile?.residence ||
                              profile?.lieuResidence ||
                              "—"
                            }
                          />
                          <DetailItem
                            label="Téléphone"
                            value={phoneValue || "—"}
                          />
                          <DetailItem
                            label="Email"
                            value={profile?.email || user.email || "—"}
                          />
                        </div>

                        <SectionTitle
                          icon="☑️"
                          title="Vos disponibilités régulières"
                        />

                        {disponibilites.length > 0 ? (
                          <div style={styles.availabilityList}>
                            {disponibilites.map((item, index) => (
                              <div key={index} style={styles.availabilityItem}>
                                <span style={styles.dayBadge}>{item.jour}</span>
                                <span style={styles.timeBadge}>
                                  {item.horaire}
                                </span>
                                <span style={styles.availabilityText}>
                                  {(item.structure || "Étude Alpha") +
                                    " · " +
                                    (item.lieu || "Non précisé")}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={styles.emptyInfoBox}>
                            Aucune disponibilité régulière renseignée pour le
                            moment.
                          </div>
                        )}

                        <SectionTitle
                          icon="🗓️"
                          title="Disponibilités du calendrier"
                        />

                        <div style={styles.calendarHeader}>
                          <button
                            type="button"
                            onClick={goToPreviousMonth}
                            style={styles.monthNavBtn}
                          >
                            ←
                          </button>

                          <div style={styles.calendarMonthLabel}>
                            {getMonthLabel(calendarMonth)}
                          </div>

                          <button
                            type="button"
                            onClick={goToNextMonth}
                            style={styles.monthNavBtn}
                          >
                            →
                          </button>
                        </div>

                        <div style={styles.slotSelectorBox}>
                          <label style={styles.fieldLabel}>
                            Créneau à appliquer lors du clic
                          </label>
                          <select
                            value={selectedSlot}
                            onChange={(e) => setSelectedSlot(e.target.value)}
                            style={styles.selectInput}
                            className="select-fancy"
                          >
                            {TIME_SLOTS.map((slot) => (
                              <option key={slot} value={slot}>
                                {slot}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div style={styles.calendarCard}>
                          <div style={styles.calendarWeekHeader}>
                            {WEEK_DAYS.map((day) => (
                              <div key={day} style={styles.calendarWeekCell}>
                                {day}
                              </div>
                            ))}
                          </div>

                          <div
                            style={styles.calendarGrid}
                            className="calendar-grid-responsive"
                          >
                            {calendarDays.map((date, index) => {
                              if (!date) {
                                return (
                                  <div
                                    key={`empty-${index}`}
                                    style={styles.calendarEmptyCell}
                                  />
                                );
                              }

                              const key = formatDateKey(date);
                              const isSelected = Boolean(calendarAvailability[key]);
                              const isToday = formatDateKey(new Date()) === key;

                              return (
                                <button
                                  key={key}
                                  type="button"
                                  onClick={() => toggleCalendarDay(date)}
                                  style={{
                                    ...styles.calendarDayBtn,
                                    ...(isSelected
                                      ? styles.calendarDayBtnSelected
                                      : {}),
                                    ...(isToday ? styles.calendarDayBtnToday : {}),
                                  }}
                                >
                                  <span style={styles.calendarDayNumber}>
                                    {date.getDate()}
                                  </span>
                                  {isSelected && (
                                    <span style={styles.calendarDaySlot}>
                                      {calendarAvailability[key]?.horaire}
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {sortedCalendarAvailability.length > 0 ? (
                          <div style={styles.selectedDaysBox}>
                            <div style={styles.selectedDaysTitle}>
                              Jours sélectionnés
                            </div>

                            <div style={styles.selectedDaysList}>
                              {sortedCalendarAvailability.map(
                                ([dateKey, data]) => (
                                  <div
                                    key={dateKey}
                                    style={styles.selectedDayItem}
                                  >
                                    <div style={styles.selectedDayLeft}>
                                      <div style={styles.selectedDayDate}>
                                        {formatPrettyDate(dateKey)}
                                      </div>

                                      <select
                                        value={data?.horaire || TIME_SLOTS[0]}
                                        onChange={(e) =>
                                          updateAvailabilitySlot(
                                            dateKey,
                                            e.target.value
                                          )
                                        }
                                        style={styles.selectedDaySelect}
                                        className="select-fancy"
                                      >
                                        {TIME_SLOTS.map((slot) => (
                                          <option key={slot} value={slot}>
                                            {slot}
                                          </option>
                                        ))}
                                      </select>
                                    </div>

                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeAvailabilityDate(dateKey)
                                      }
                                      style={styles.removeDayBtn}
                                    >
                                      Retirer
                                    </button>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        ) : (
                          <div style={styles.emptyInfoBox}>
                            Cliquez sur un ou plusieurs jours dans le calendrier
                            pour indiquer vos disponibilités.
                          </div>
                        )}

                        <div style={styles.actionsRow}>
                          <button
                            style={styles.primaryButton}
                            className="primary-btn-fancy"
                            onClick={handleSaveProfile}
                            disabled={savingProfile}
                          >
                            {savingProfile
                              ? "Enregistrement..."
                              : "Enregistrer mes disponibilités"}
                          </button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </>
        );

      case "upcoming":
        return (
          <SimpleSection
            title="Séances à venir"
            subtitle="Retrouvez ici vos prochaines séances et opportunités."
          >
            <div style={styles.placeholderGrid}>
              <PlaceholderCard
                title="Planning"
                text="Les prochaines séances apparaîtront ici."
                accent="#17233c"
              />
              <PlaceholderCard
                title="Opportunités"
                text="Vous pourrez vous positionner sur des séances disponibles."
                accent="#a8792a"
              />
            </div>
          </SimpleSection>
        );

      case "past":
        return (
          <SimpleSection
            title="Séances passées"
            subtitle="Consultez l'historique de vos interventions."
          >
            <div style={styles.placeholderGrid}>
              <PlaceholderCard
                title="Historique"
                text="Vos séances passées et bilans seront affichés ici."
                accent="#1f7a5c"
              />
            </div>
          </SimpleSection>
        );

      case "reviews":
        return (
          <SimpleSection
            title="Mes avis"
            subtitle="Consultez ici vos évaluations et retours."
          >
            <div style={styles.placeholderGrid}>
              <PlaceholderCard
                title="Évaluations"
                text="Les avis et notations apparaîtront ici."
                accent="#b3261e"
              />
            </div>
          </SimpleSection>
        );

      case "chatgpt":
        return (
          <SimpleSection
            title=""
            subtitle="Accès rapide à l'assistance et aux échanges."
          >
            <div style={styles.placeholderGrid}>
              <PlaceholderCard
                title="Assistant"
                text="Vous pouvez poser vos questions ou contacter l'équipe."
                accent="#5a5f6c"
              />
              <button
                style={styles.helpButton}
                onClick={() => navigate("/contact")}
              >
                Ouvrir le support
              </button>
            </div>
          </SimpleSection>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <style>{GLOBAL_CSS}</style>

      <div style={styles.page} className="page-padding-responsive profile-shell">
        <div style={styles.layout} className="layout-responsive">
          <aside
            style={styles.sidebar}
            className="sidebar-responsive ui-hover-lift"
          >
            <div style={styles.sidebarTop}>
              <div style={styles.sidebarLogo}>H</div>
              <div>
                <div style={styles.sidebarTitle}>Mon espace</div>
                <div style={styles.sidebarSubtitle}>
                  Tableau de bord personnel
                </div>
              </div>
            </div>

            <div style={styles.sidebarGroup}>
              <SideItem
                active={activeTab === "home"}
                icon="🏠"
                label="Home"
                onClick={() => setActiveTab("home")}
              />
              <SideItem
                active={activeTab === "profile"}
                icon="🪪"
                label="Profil"
                onClick={() => setActiveTab("profile")}
              />
              <SideItem
                active={activeTab === "upcoming"}
                icon="🏛️"
                label="Séances à venir"
                onClick={() => setActiveTab("upcoming")}
              />
              <SideItem
                active={activeTab === "past"}
                icon="🏛️"
                label="Séances passées"
                onClick={() => setActiveTab("past")}
              />

              <SideInfo>
                Positionnez-vous dès maintenant sur des séances disponibles !
              </SideInfo>

              <SideItem
                active={activeTab === "reviews"}
                icon="📊"
                label="Avis"
                onClick={() => setActiveTab("reviews")}
              />
              <SideItem
                active={activeTab === "chatgpt"}
                icon="🤖"
                label="Assistance"
                onClick={() => setActiveTab("chatgpt")}
              />
            </div>
          </aside>

          <section style={styles.content}>{renderMainContent()}</section>
        </div>
      </div>
    </>
  );
}

function SideItem({ icon, label, active = false, onClick }) {
  const [hovered, setHovered] = React.useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="sidebar-fancy-btn"
      style={{
        ...styles.sideItem,
        ...(hovered && !active ? styles.sideItemHover : {}),
        ...(active ? styles.sideItemActive : {}),
      }}
    >
      <span style={styles.sideIcon}>{icon}</span>
      <span>{label}</span>
    </button>
  );
}

function SideInfo({ children }) {
  return <div style={styles.sideInfo}>{children}</div>;
}

function DashboardCard({
  title,
  tag,
  action,
  subtitle,
  icon,
  iconBg,
  accent,
  onClick,
}) {
  return (
    <button
      onClick={onClick}
      style={styles.dashboardCard}
      className="dashboard-card-fancy"
    >
      <div style={styles.dashboardTop}>
        <span style={styles.dashboardTitle}>{title}</span>
        <span style={styles.dashboardTag}>| {tag}</span>
      </div>

      <div style={styles.dashboardBody}>
        <div style={{ ...styles.dashboardIcon, background: iconBg }}>
          {icon}
        </div>
        <div style={{ textAlign: "left" }}>
          <div style={{ ...styles.dashboardAction, color: accent }}>
            {action}
          </div>
          <div style={styles.dashboardSubtitle}>{subtitle}</div>
        </div>
      </div>
    </button>
  );
}

function SimpleSection({ title, subtitle, children }) {
  return (
    <div style={styles.sectionCard} className="ui-hover-lift">
      <div style={{ marginBottom: 14 }}>
        <h2 style={styles.profileTitle}>{title}</h2>
        <p style={styles.profileSubtitle}>{subtitle}</p>
      </div>
      {children}
    </div>
  );
}

function PlaceholderCard({ title, text, accent }) {
  return (
    <div
      style={{ ...styles.placeholderCard, borderLeft: `4px solid ${accent}` }}
    >
      <div style={styles.placeholderTitle}>{title}</div>
      <div style={styles.placeholderText}>{text}</div>
    </div>
  );
}

function SectionTitle({ icon, title }) {
  return (
    <div style={styles.sectionTitleRow}>
      <span style={styles.sectionTitleIcon}>{icon}</span>
      <h3 style={styles.inlineSectionTitle}>{title}</h3>
    </div>
  );
}

function DetailItem({ label, value }) {
  return (
    <div style={styles.detailRow} className="details-row-responsive">
      <div style={styles.detailLabel}>{label}</div>
      <div style={styles.detailValue}>{value}</div>
    </div>
  );
}

function InputField({
  label,
  value,
  onChange,
  type = "text",
  disabled = false,
}) {
  return (
    <div style={styles.field}>
      <label style={styles.fieldLabel}>{label}</label>
      <input
        className="field-input-fancy"
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange && onChange(e.target.value)}
        disabled={disabled}
        style={{
          ...styles.fieldInput,
          ...(disabled ? styles.fieldInputDisabled : {}),
        }}
      />
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "var(--bg)",
    padding: "18px",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },

  layout: {
    maxWidth: "1280px",
    margin: "0 auto",
    display: "grid",
    gridTemplateColumns: "240px 1fr",
    gap: "20px",
    alignItems: "start",
  },

  sidebar: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: "16px",
    padding: "14px",
    boxShadow: "0 8px 24px rgba(23,35,60,0.04)",
    position: "sticky",
    top: "92px",
  },

  sidebarTop: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "6px 6px 14px",
    marginBottom: "8px",
    borderBottom: "1px solid var(--border-soft)",
  },

  sidebarLogo: {
    width: "38px",
    height: "38px",
    borderRadius: "10px",
    background: "var(--navy)",
    color: "var(--gold-soft-2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'Source Serif 4', serif",
    fontWeight: "700",
    fontSize: "18px",
    border: "1px solid var(--gold)",
  },

  sidebarTitle: {
    fontSize: "14px",
    fontWeight: "700",
    color: "var(--ink)",
  },

  sidebarSubtitle: {
    fontSize: "11.5px",
    color: "var(--muted)",
    marginTop: "2px",
    fontFamily: "'IBM Plex Mono', monospace",
    letterSpacing: "0.02em",
  },

  sidebarGroup: {
    display: "grid",
    gap: "8px",
  },

  sideItem: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    width: "100%",
    padding: "11px 14px",
    borderRadius: "10px",
    border: "1px solid transparent",
    borderLeft: "3px solid transparent",
    background: "transparent",
    cursor: "pointer",
    color: "var(--ink-soft)",
    fontSize: "13.5px",
    fontWeight: "600",
    textAlign: "left",
  },

  sideItemHover: {
    background: "var(--surface-2)",
    border: "1px solid var(--border-soft)",
    borderLeft: "3px solid transparent",
  },

  sideItemActive: {
    background: "var(--navy-soft)",
    color: "var(--navy)",
    border: "1px solid transparent",
    borderLeft: "3px solid var(--gold)",
    fontWeight: "700",
  },

  sideIcon: {
    width: "20px",
    minWidth: "20px",
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    fontSize: "16px",
  },

  sideInfo: {
    fontSize: "12px",
    lineHeight: "1.6",
    color: "var(--ink-soft)",
    background: "var(--gold-soft-2)",
    border: "1px solid var(--gold-soft)",
    padding: "12px",
    borderRadius: "10px",
    textAlign: "center",
    margin: "4px 0",
  },

  content: {
    minWidth: 0,
  },

  topBackButton: {
    border: "none",
    background: "transparent",
    color: "var(--ink-soft)",
    fontSize: "13.5px",
    fontWeight: "700",
    cursor: "pointer",
    marginBottom: "14px",
    padding: 0,
  },

  welcomeBlock: {
    background: "var(--surface)",
    borderRadius: "16px",
    padding: "20px",
    marginBottom: "18px",
    border: "1px solid var(--border)",
  },

  welcomeHeader: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
  },

  welcomeIconWrap: {
    width: "52px",
    height: "52px",
    borderRadius: "12px",
    background: "var(--navy-soft)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  welcomeIcon: {
    fontSize: "22px",
    lineHeight: 1,
  },

  pageTitle: {
    margin: 0,
    fontFamily: "'Source Serif 4', serif",
    fontSize: "26px",
    color: "var(--ink)",
    fontWeight: "600",
    lineHeight: 1.15,
  },

  pageSubtitle: {
    margin: "6px 0 0",
    color: "var(--muted)",
    fontSize: "13.5px",
  },

  cardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "14px",
    marginBottom: "18px",
  },

  dashboardCard: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: "14px",
    padding: "16px",
    boxShadow: "0 6px 18px rgba(23,35,60,0.03)",
    cursor: "pointer",
    textAlign: "left",
  },

  dashboardTop: {
    display: "flex",
    alignItems: "baseline",
    gap: "6px",
    marginBottom: "12px",
    flexWrap: "wrap",
  },

  dashboardTitle: {
    fontSize: "14.5px",
    fontWeight: "700",
    color: "var(--ink)",
  },

  dashboardTag: {
    color: "var(--muted)",
    fontSize: "11.5px",
    fontWeight: "600",
    fontFamily: "'IBM Plex Mono', monospace",
  },

  dashboardBody: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },

  dashboardIcon: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "22px",
    flexShrink: 0,
  },

  dashboardAction: {
    fontSize: "13.5px",
    fontWeight: "700",
    marginBottom: "3px",
  },

  dashboardSubtitle: {
    color: "var(--muted)",
    fontSize: "12.5px",
    lineHeight: "1.5",
  },

  breadcrumbRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "10px",
    fontSize: "12.5px",
    fontFamily: "'IBM Plex Mono', monospace",
  },

  breadcrumbLink: {
    color: "var(--muted)",
    fontWeight: "600",
  },

  breadcrumbSep: {
    color: "var(--muted)",
  },

  breadcrumbCurrent: {
    color: "var(--gold)",
    fontWeight: "700",
  },

  heroCard: {
    borderRadius: "16px",
    padding: "20px 22px",
    marginBottom: "16px",
    background: "var(--navy)",
    border: "1px solid var(--navy)",
    boxShadow: "0 10px 26px rgba(23,35,60,0.14)",
  },

  profileHeaderBlock: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
  },

  profileHeaderIconWrap: {
    width: "50px",
    height: "50px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(255,255,255,0.08)",
    border: "1px solid var(--gold)",
    flexShrink: 0,
  },

  profileHeaderIcon: {
    fontSize: "22px",
    lineHeight: 1,
  },

  pageTitleAlt: {
    margin: 0,
    fontFamily: "'Source Serif 4', serif",
    fontSize: "30px",
    color: "#f7f4ea",
    fontWeight: "600",
    lineHeight: 1.1,
  },

  pageSubtitleAlt: {
    margin: "6px 0 0",
    color: "rgba(247,244,234,0.68)",
    fontSize: "13.5px",
  },

  profileLayout: {
    display: "grid",
    gridTemplateColumns: "250px 1fr",
    gap: "18px",
    alignItems: "start",
  },

  profileSideCard: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: "16px",
    padding: "20px 18px",
    textAlign: "center",
    boxShadow: "0 6px 18px rgba(23,35,60,0.03)",
  },

  bigAvatarWrap: {
    width: "92px",
    height: "92px",
    borderRadius: "50%",
    overflow: "hidden",
    margin: "0 auto 14px",
    background: "var(--navy)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "3px solid var(--gold)",
    boxShadow: "0 0 0 4px var(--gold-soft-2)",
  },

  bigAvatarImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },

  bigAvatarFallback: {
    color: "#f7f4ea",
    fontFamily: "'Source Serif 4', serif",
    fontSize: "30px",
    fontWeight: "600",
  },

  sideName: {
    fontFamily: "'Source Serif 4', serif",
    fontSize: "19px",
    fontWeight: "600",
    color: "var(--ink)",
    marginBottom: "6px",
    lineHeight: 1.25,
  },

  sideMeta: {
    fontSize: "12.5px",
    color: "var(--muted)",
    marginBottom: "14px",
  },

  sideMiniStats: {
    display: "grid",
    gap: "8px",
    marginTop: "14px",
  },

  sideMiniStat: {
    background: "var(--surface-2)",
    border: "1px solid var(--border-soft)",
    borderRadius: "10px",
    padding: "10px 12px",
    textAlign: "left",
  },

  sideMiniStatLabel: {
    display: "block",
    fontSize: "10.5px",
    fontWeight: "700",
    color: "var(--muted)",
    marginBottom: "4px",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontFamily: "'IBM Plex Mono', monospace",
  },

  sideMiniStatValue: {
    display: "block",
    fontSize: "13px",
    fontWeight: "700",
    color: "var(--ink)",
    lineHeight: "1.4",
    wordBreak: "break-word",
  },

  profileMainCard: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: "16px",
    padding: "18px 18px 20px",
    boxShadow: "0 6px 18px rgba(23,35,60,0.03)",
  },

  profileTabs: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    paddingBottom: "14px",
    borderBottom: "1px solid var(--border-soft)",
    marginBottom: "16px",
    flexWrap: "wrap",
  },

  profileTabBtn: {
    border: "1px solid var(--border)",
    background: "var(--surface)",
    color: "var(--ink-soft)",
    fontSize: "12.5px",
    fontWeight: "700",
    cursor: "pointer",
    padding: "10px 14px",
    borderRadius: "8px",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
  },

  profileTabBtnActive: {
    background: "var(--navy)",
    color: "#f7f4ea",
    border: "1px solid var(--navy)",
  },

  alertError: {
    marginBottom: "14px",
    padding: "12px 14px",
    borderRadius: "10px",
    background: "var(--err-soft)",
    border: "1px solid rgba(179,38,30,0.28)",
    color: "var(--err)",
    fontWeight: "700",
    fontSize: "13px",
  },

  alertSuccess: {
    marginBottom: "14px",
    padding: "12px 14px",
    borderRadius: "10px",
    background: "var(--ok-soft)",
    border: "1px solid rgba(31,122,92,0.28)",
    color: "var(--ok)",
    fontWeight: "700",
    fontSize: "13px",
  },

  sectionTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginTop: "14px",
    marginBottom: "10px",
  },

  sectionTitleIcon: {
    width: "28px",
    height: "28px",
    borderRadius: "8px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "var(--navy-soft)",
    fontSize: "14px",
    flexShrink: 0,
  },

  inlineSectionTitle: {
    margin: 0,
    fontFamily: "'Source Serif 4', serif",
    fontSize: "17px",
    fontWeight: "600",
    color: "var(--ink)",
  },

  passwordIntroBox: {
    background: "var(--surface-2)",
    border: "1px solid var(--border-soft)",
    borderRadius: "10px",
    padding: "12px 14px",
    fontSize: "13px",
    color: "var(--ink-soft)",
    lineHeight: "1.6",
    marginBottom: "14px",
  },

  cursusBox: {
    background: "var(--surface-2)",
    border: "1px solid var(--border)",
    borderRadius: "10px",
    padding: "14px",
    fontSize: "13.5px",
    color: "var(--ink-soft)",
    lineHeight: "1.75",
    fontStyle: "italic",
    marginBottom: "14px",
  },

  detailsGrid: {
    display: "grid",
    gap: "8px",
    marginBottom: "16px",
  },

  detailRow: {
    display: "grid",
    gridTemplateColumns: "180px 1fr",
    gap: "12px",
    alignItems: "center",
    background: "var(--surface-2)",
    border: "1px solid var(--border-soft)",
    borderRadius: "10px",
    padding: "12px 14px",
  },

  detailLabel: {
    fontSize: "11px",
    color: "var(--muted)",
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    fontFamily: "'IBM Plex Mono', monospace",
  },

  detailValue: {
    fontSize: "14px",
    color: "var(--ink)",
    fontWeight: "700",
    wordBreak: "break-word",
  },

  availabilityList: {
    display: "grid",
    gap: "10px",
  },

  availabilityItem: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
    background: "var(--surface-2)",
    border: "1px solid var(--border-soft)",
    borderRadius: "10px",
    padding: "10px 12px",
  },

  dayBadge: {
    background: "var(--navy)",
    color: "#f7f4ea",
    borderRadius: "999px",
    padding: "5px 10px",
    fontSize: "10.5px",
    fontWeight: "700",
    fontFamily: "'IBM Plex Mono', monospace",
  },

  timeBadge: {
    background: "var(--gold-soft)",
    color: "var(--gold)",
    borderRadius: "999px",
    padding: "5px 10px",
    fontSize: "10.5px",
    fontWeight: "700",
    fontFamily: "'IBM Plex Mono', monospace",
  },

  availabilityText: {
    fontSize: "12.5px",
    color: "var(--ink-soft)",
    fontWeight: "600",
  },

  viewAvailabilityList: {
    display: "grid",
    gap: "10px",
  },

  viewAvailabilityItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
    background: "var(--surface-2)",
    border: "1px solid var(--border-soft)",
    borderRadius: "10px",
    padding: "12px 14px",
  },

  viewAvailabilityDate: {
    fontSize: "13px",
    fontWeight: "700",
    color: "var(--ink)",
  },

  viewAvailabilitySlot: {
    fontSize: "12.5px",
    fontWeight: "700",
    color: "var(--navy)",
    background: "var(--navy-soft)",
    borderRadius: "999px",
    padding: "6px 10px",
  },

  emptyInfoBox: {
    background: "var(--surface-2)",
    border: "1px dashed var(--border)",
    borderRadius: "10px",
    padding: "14px",
    textAlign: "center",
    color: "var(--muted)",
    fontSize: "13px",
    lineHeight: "1.6",
  },

  profileTitle: {
    margin: 0,
    fontFamily: "'Source Serif 4', serif",
    fontSize: "22px",
    fontWeight: "600",
    color: "var(--ink)",
  },

  profileSubtitle: {
    margin: "6px 0 0",
    color: "var(--muted)",
    fontSize: "13.5px",
  },

  sectionCard: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: "16px",
    padding: "18px",
    marginBottom: "18px",
    boxShadow: "0 6px 18px rgba(23,35,60,0.03)",
  },

  placeholderGrid: {
    display: "grid",
    gap: "14px",
  },

  placeholderCard: {
    background: "var(--surface-2)",
    border: "1px solid var(--border-soft)",
    borderRadius: "10px",
    padding: "14px",
  },

  placeholderTitle: {
    fontSize: "14.5px",
    fontWeight: "700",
    color: "var(--ink)",
    marginBottom: "6px",
  },

  placeholderText: {
    fontSize: "13px",
    color: "var(--muted)",
    lineHeight: "1.6",
  },

  helpButton: {
    background: "var(--navy)",
    color: "#f7f4ea",
    border: "none",
    borderRadius: "10px",
    padding: "12px 16px",
    fontWeight: "700",
    fontSize: "13.5px",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "14px",
    marginTop: "6px",
  },

  field: {
    display: "grid",
    gap: "6px",
  },

  fieldLabel: {
    fontSize: "11.5px",
    color: "var(--ink-soft)",
    fontWeight: "700",
  },

  fieldInput: {
    width: "100%",
    height: "42px",
    borderRadius: "8px",
    border: "1px solid var(--border)",
    background: "var(--surface-2)",
    padding: "0 12px",
    fontSize: "13px",
    color: "var(--ink)",
    fontWeight: "600",
  },

  fieldInputDisabled: {
    background: "var(--border-soft)",
    color: "var(--muted)",
    cursor: "not-allowed",
  },

  textarea: {
    width: "100%",
    minHeight: "120px",
    borderRadius: "10px",
    border: "1px solid var(--border)",
    background: "var(--surface-2)",
    padding: "12px 14px",
    fontSize: "13.5px",
    color: "var(--ink)",
    lineHeight: "1.7",
    resize: "vertical",
    marginBottom: "14px",
    fontFamily: "inherit",
  },

  selectInput: {
    width: "100%",
    height: "40px",
    borderRadius: "8px",
    border: "1px solid var(--border)",
    background: "var(--surface-2)",
    padding: "0 12px",
    fontSize: "13px",
    color: "var(--ink)",
    fontWeight: "600",
  },

  actionsRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    marginTop: "16px",
    flexWrap: "wrap",
  },

  primaryButton: {
    border: "1px solid var(--navy)",
    background: "var(--navy)",
    color: "#f7f4ea",
    borderRadius: "8px",
    padding: "12px 16px",
    fontSize: "13px",
    fontWeight: "700",
    cursor: "pointer",
  },

  secondaryButton: {
    border: "1px solid var(--border)",
    background: "var(--surface)",
    color: "var(--ink-soft)",
    borderRadius: "8px",
    padding: "12px 16px",
    fontSize: "13px",
    fontWeight: "700",
    cursor: "pointer",
  },

  calendarHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    marginTop: "10px",
    marginBottom: "12px",
  },

  monthNavBtn: {
    width: "36px",
    height: "36px",
    borderRadius: "8px",
    border: "1px solid var(--border)",
    background: "var(--surface)",
    color: "var(--ink)",
    fontSize: "15px",
    fontWeight: "700",
    cursor: "pointer",
  },

  calendarMonthLabel: {
    fontFamily: "'Source Serif 4', serif",
    fontSize: "16px",
    fontWeight: "600",
    color: "var(--ink)",
    textTransform: "capitalize",
  },

  slotSelectorBox: {
    marginBottom: "12px",
  },

  calendarCard: {
    background: "var(--surface-2)",
    border: "1px solid var(--border-soft)",
    borderRadius: "14px",
    padding: "12px",
  },

  calendarWeekHeader: {
    display: "grid",
    gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
    gap: "8px",
    marginBottom: "8px",
  },

  calendarWeekCell: {
    textAlign: "center",
    fontSize: "11px",
    fontWeight: "700",
    color: "var(--muted)",
    padding: "6px 0",
    fontFamily: "'IBM Plex Mono', monospace",
  },

  calendarGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
    gap: "8px",
  },

  calendarEmptyCell: {
    minHeight: "60px",
  },

  calendarDayBtn: {
    minHeight: "60px",
    borderRadius: "10px",
    border: "1px solid var(--border)",
    background: "var(--surface)",
    cursor: "pointer",
    padding: "8px 6px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
  },

  calendarDayBtnSelected: {
    background: "var(--navy)",
    border: "1px solid var(--navy)",
    color: "#f7f4ea",
  },

  calendarDayBtnToday: {
    boxShadow: "0 0 0 2px var(--gold)",
  },

  calendarDayNumber: {
    fontSize: "13.5px",
    fontWeight: "700",
    lineHeight: 1,
  },

  calendarDaySlot: {
    fontSize: "9.5px",
    fontWeight: "700",
    textAlign: "center",
    lineHeight: "1.25",
    opacity: 0.95,
    fontFamily: "'IBM Plex Mono', monospace",
  },

  selectedDaysBox: {
    marginTop: "14px",
    background: "var(--surface-2)",
    border: "1px solid var(--border-soft)",
    borderRadius: "14px",
    padding: "14px",
  },

  selectedDaysTitle: {
    fontSize: "13.5px",
    fontWeight: "700",
    color: "var(--ink)",
    marginBottom: "10px",
  },

  selectedDaysList: {
    display: "grid",
    gap: "10px",
  },

  selectedDayItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    flexWrap: "wrap",
    background: "var(--surface)",
    border: "1px solid var(--border-soft)",
    borderRadius: "10px",
    padding: "10px 12px",
  },

  selectedDayLeft: {
    display: "grid",
    gap: "8px",
    flex: 1,
    minWidth: "220px",
  },

  selectedDayDate: {
    fontSize: "13px",
    fontWeight: "700",
    color: "var(--ink)",
    textTransform: "capitalize",
  },

  selectedDaySelect: {
    width: "100%",
    maxWidth: "220px",
    height: "38px",
    borderRadius: "8px",
    border: "1px solid var(--border)",
    background: "var(--surface-2)",
    padding: "0 12px",
    fontSize: "13px",
    color: "var(--ink)",
    fontWeight: "600",
  },

  removeDayBtn: {
    border: "1px solid rgba(179,38,30,0.28)",
    background: "var(--err-soft)",
    color: "var(--err)",
    borderRadius: "8px",
    padding: "10px 12px",
    fontSize: "12px",
    fontWeight: "700",
    cursor: "pointer",
  },

  loadingPage: {
    minHeight: "100vh",
    background: "var(--bg)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "24px",
  },

  loadingCard: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    padding: "32px 28px",
    borderRadius: "16px",
    boxShadow: "0 16px 40px rgba(23,35,60,0.08)",
    textAlign: "center",
    minWidth: "260px",
  },

  loader: {
    width: "42px",
    height: "42px",
    border: "4px solid var(--gold)",
    borderTopColor: "transparent",
    borderRadius: "50%",
    margin: "0 auto 16px",
    animation: "spin 1s linear infinite",
  },

  loadingText: {
    fontSize: "14.5px",
    fontWeight: "700",
    color: "var(--ink-soft)",
    margin: 0,
  },

  emptyCard: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    padding: "40px 32px",
    borderRadius: "16px",
    boxShadow: "0 16px 40px rgba(23,35,60,0.08)",
    textAlign: "center",
    maxWidth: "420px",
    width: "100%",
  },

  emptyIcon: {
    width: "72px",
    height: "72px",
    background: "var(--navy)",
    borderRadius: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 20px",
    fontSize: "30px",
    color: "#f7f4ea",
    border: "1px solid var(--gold)",
  },

  emptyTitle: {
    fontFamily: "'Source Serif 4', serif",
    fontSize: "21px",
    fontWeight: "600",
    color: "var(--ink)",
    marginBottom: "10px",
  },

  emptyText: {
    fontSize: "14.5px",
    color: "var(--muted)",
    lineHeight: "1.6",
    margin: 0,
  },
};